// Admin API types

export interface OverviewStats {
  total_tenants: number
  active_tenants: number
  new_tenants_this_month: number
  total_verified_bookings: number
  pending_proof_review: number
  pending_upgrade_requests: number
  total_revenue_idr: number
}

export interface PlanInfo {
  id: string
  name: string
  slug: string
  price_idr: number | null
}

export interface SubscriptionInfo {
  status: 'active' | 'trial' | 'suspended' | 'cancelled'
  started_at: string
  expires_at: string | null
  notes: string | null
  plans: PlanInfo | null
}

export interface TenantListItem {
  id: string
  slug: string
  name: string
  logo_url: string | null
  is_active: boolean
  created_at: string
  tenant_subscriptions: SubscriptionInfo | null
}

export interface TenantStats {
  total_events: number
  total_members: number
  total_verified_bookings: number
  total_revenue_idr: number
}

export interface TenantDetail extends TenantListItem {
  description: string | null
  banner_url: string | null
  owner_id: string | null
  tenant_subscriptions: (SubscriptionInfo & { id: string }) | null
  stats: TenantStats
}

export interface PlanFeature {
  feature_key: string
}

export interface Plan {
  id: string
  name: string
  slug: string
  description: string | null
  price_idr: number | null
  is_active: boolean
  sort_order: number
  created_at: string
  plan_features: PlanFeature[]
}

export interface ChartDataPoint {
  month: string               // "YYYY-MM"
  new_tenants: number
  verified_bookings: number
  revenue_idr: number
}

export interface InactiveTenant {
  id: string
  name: string
  slug: string
  created_at: string
}

export interface AnalyticsData {
  chart_data: ChartDataPoint[]
  inactive_tenants: InactiveTenant[]
}

// Request payloads

export interface CreateTenantPayload {
  name: string
  slug: string
  description?: string
  owner_email?: string
}

export interface UpdateTenantPayload {
  name?: string
  description?: string
  is_active?: boolean
}

export interface AssignPlanPayload {
  plan_id: string
  status?: 'active' | 'trial' | 'suspended' | 'cancelled'
  expires_at?: string | null
  notes?: string
}

// ── Registration Requests ─────────────────────────────────────────────────────

export interface RegistrationRequest {
  id: string
  group_name: string
  slug: string
  owner_email: string
  whatsapp: string | null
  status: 'pending' | 'approved' | 'rejected'
  trial_days: number
  notes: string | null
  reviewed_by: string | null
  reviewed_at: string | null
  tenant_id: string | null
  created_at: string
  updated_at: string
}

// ── Subscription Upgrade Requests ─────────────────────────────────────────────

export interface UpgradeRequest {
  id: string
  tenant_id: string
  current_plan_id: string | null
  requested_plan_id: string
  proof_url: string | null
  status: 'pending' | 'proof_uploaded' | 'approved' | 'rejected'
  admin_note: string | null
  verified_at: string | null
  created_at: string
  updated_at: string
  /** The requested (target) plan. */
  plans: { id: string; name: string; slug: string; price_idr: number | null; sort_order: number } | null
  /** The plan the tenant was on at the time of the request (null = no prior plan). */
  current_plan: { id: string; name: string; slug: string; sort_order: number } | null
  tenants: { id: string; name: string; slug: string } | null
}

export interface CreatePlanPayload {
  name: string
  slug: string
  description?: string
  price_idr?: number | null
  sort_order?: number
  features: string[]
}

export interface UpdatePlanPayload {
  name?: string
  description?: string
  price_idr?: number | null
  is_active?: boolean
  sort_order?: number
  features?: string[]
}

// ── Component Catalog Types ───────────────────────────────────────────────────

export interface FieldDef {
  key: string
  label: string
  type: 'text' | 'number' | 'boolean' | 'select' | 'url' | 'textarea'
  options?: { value: string; label: string }[]
  placeholder?: string
}

export interface PageComponent {
  id: string
  component_key: string
  category: string
  label: string
  description: string
  preview?: string | null
  default_settings: Record<string, unknown>
  settings_schema: FieldDef[]
  data_requirement: string | null
  page_scope: string | null
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
  component_plan_access: Array<{ plan_id: string }>
}

export interface CreateComponentPayload {
  component_key: string
  category: string
  label: string
  description?: string
  preview?: string
  default_settings?: Record<string, unknown>
  settings_schema?: FieldDef[]
  data_requirement?: string | null
  page_scope?: string | null
  sort_order?: number
  is_active?: boolean
}

export interface UpdateComponentPayload {
  label?: string
  description?: string
  category?: string
  default_settings?: Record<string, unknown>
  settings_schema?: FieldDef[]
  data_requirement?: string | null
  page_scope?: string | null
  is_active?: boolean
  sort_order?: number
}

export const COMPONENT_CATEGORIES = [
  'Hero',
  'CTA',
  'Konten Idol',
  'Media',
  'Teks',
  'Member',
] as const

export const DATA_REQUIREMENTS = [
  { value: 'members', label: 'Members' },
  { value: 'events',  label: 'Events' },
  { value: 'cheki',   label: 'Cheki' },
  { value: 'music',   label: 'Music' },
  { value: 'merch',   label: 'Merch' },
  { value: 'faqs',    label: 'FAQs' },
  { value: 'member',  label: 'Member (single)' },
] as const

export const PAGE_SCOPES = [
  { value: 'member-detail', label: 'Halaman Detail Member' },
] as const

export const FEATURE_GROUPS = ['Konten', 'Cheki', 'Halaman Publik'] as const
export type FeatureGroup = (typeof FEATURE_GROUPS)[number]

export const FEATURE_KEYS = [
  { key: 'members',         label: 'Manajemen Member', group: 'Konten'        },
  { key: 'events',          label: 'Manajemen Event',  group: 'Konten'        },
  { key: 'music',           label: 'Musik',            group: 'Konten'        },
  { key: 'shop',            label: 'Shop',             group: 'Konten'        },
  { key: 'media',           label: 'Media Library',    group: 'Konten'        },

  { key: 'cheki_form',      label: 'Cheki Form',       group: 'Cheki'         },
  { key: 'cheki_analytics', label: 'Cheki Analitik',   group: 'Cheki'         },

  { key: 'theme_preset',    label: 'Tema Preset',      group: 'Halaman Publik' },
  { key: 'custom_theme',    label: 'Tema Visual',      group: 'Halaman Publik' },
  { key: 'gallery',         label: 'Galeri Komponen',  group: 'Halaman Publik' },
  { key: 'custom_pages',    label: 'Halaman Kustom',   group: 'Halaman Publik' },
] as const

// ── Theme Presets ─────────────────────────────────────────────────────────────

export interface BlockConfig {
  id: string
  component: string
  enabled: boolean
  order: number
  settings: Record<string, unknown>
}

export interface ThemePreset {
  id: string
  name: string
  description: string | null
  thumbnail_url: string | null
  is_active: boolean
  sort_order: number
  palette_group: string | null
  mode: 'dark' | 'light' | 'any'
  color_primary: string
  color_secondary: string
  color_accent: string
  color_background: string
  color_surface: string
  color_text_primary: string
  color_text_muted: string
  color_border: string
  font_family_heading: string
  font_family_body: string
  border_radius_base: string
  custom_css: string | null
  homepage_sections: BlockConfig[]
  page_about: BlockConfig[]
  page_events: BlockConfig[]
  page_members: BlockConfig[]
  page_music: BlockConfig[]
  page_shop: BlockConfig[]
  created_at: string
  updated_at: string
}

export type PlanSlug = 'basic' | 'pro' | 'enterprise'

export interface ThemePresetSummary {
  id: string
  name: string
  description: string | null
  thumbnail_url: string | null
  is_active: boolean
  sort_order: number
  palette_group: string | null
  mode: 'dark' | 'light' | 'any'
  min_plan_slug: PlanSlug | null
  color_primary: string
  color_secondary: string
  color_accent: string
  color_background: string
  color_surface: string
  color_text_primary: string
  color_text_muted: string
  color_border: string
  font_family_heading: string
  font_family_body: string
  border_radius_base: string
}

export interface CreateThemePresetPayload {
  name: string
  description?: string | null
  thumbnail_url?: string | null
  is_active?: boolean
  sort_order?: number
  palette_group?: string | null
  mode?: 'dark' | 'light' | 'any'
  min_plan_slug?: PlanSlug | null
  color_primary: string
  color_secondary: string
  color_accent: string
  color_background: string
  color_surface: string
  color_text_primary: string
  color_text_muted: string
  color_border: string
  font_family_heading: string
  font_family_body: string
  border_radius_base: string
  custom_css?: string | null
  homepage_sections: BlockConfig[]
  page_about: BlockConfig[]
  page_events: BlockConfig[]
  page_members: BlockConfig[]
  page_music: BlockConfig[]
  page_shop: BlockConfig[]
}

export type UpdateThemePresetPayload = Partial<CreateThemePresetPayload>
